import { useCallback, useRef } from "react";

/**
 * The element the page actually scrolls inside, found by walking up from `node`.
 *
 * <p>Exported for the test. In this app the answer is `<main>` in `DashboardLayout`, which is
 * `position: fixed` with `overflowY: auto` — the window itself never scrolls, so `window.scrollTo`
 * is a no-op here and the page stays exactly where it was.
 *
 * <p>Deliberately does NOT also require `scrollHeight > clientHeight`: jsdom lays nothing out, so
 * that check would never pass in a test, and scrolling an element that happens not to be scrolled
 * is harmless.
 */
export const scrollableAncestor = (node: HTMLElement | null): HTMLElement | null => {
  for (let el = node?.parentElement ?? null; el; el = el.parentElement) {
    const { overflowY } = window.getComputedStyle(el);
    if (overflowY === "auto" || overflowY === "scroll") return el;
  }
  return null;
};

const toTop = (el: HTMLElement | Window) => {
  // jsdom implements neither, so both paths are guarded rather than assumed.
  if (typeof el.scrollTo === "function") el.scrollTo({ top: 0, behavior: "smooth" });
  else if (el !== window) (el as HTMLElement).scrollTop = 0;
};

/**
 * Returns `[ref, scrollToTop]`: put the ref on the page's root element, then call `scrollToTop()`
 * from anything that reveals something at the top of the page.
 *
 * <p>This exists because the builder's header and targets bar STICK: from halfway down a menu, the
 * link that reopens the calculator is still on screen while the calculator it opens is a screenful
 * above. Pressing it appeared to do nothing at all.
 *
 * <p>A callback ref rather than a `useRef` object, matching `useStickyHeaderHeight` — the node
 * arrives on mount and again on any re-mount, which `useRef` + `useEffect([])` silently misses.
 */
export const useScrollPageToTop = (): [(node: HTMLElement | null) => void, () => void] => {
  const node = useRef<HTMLElement | null>(null);

  const ref = useCallback((el: HTMLElement | null) => {
    node.current = el;
  }, []);

  const scrollToTop = useCallback(() => {
    toTop(scrollableAncestor(node.current) ?? window);
  }, []);

  return [ref, scrollToTop];
};

export default useScrollPageToTop;
